import React, {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import printJS from 'print-js';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import Skeleton from 'core/components/Skeleton';
import Tag from 'core/components/Tag';
import AppointmentStatusSelect from 'pages/Dashboard/components/AppointmentStatusSelect';
import Button from 'pages/Dashboard/components/Button';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Image from 'pages/Dashboard/components/Image';
import Spinner from 'pages/Dashboard/components/Spinner';
import CollapseIcon from 'core/icons/collapse';
import PrinterIcon from 'core/icons/printer';
import {
  useAppointmentsRefresh,
} from 'pages/Dashboard/hooks/usePatientSummaryCount';
import ToolTip from 'core/components/Tooltip';
import {
  ExtensionProps,
} from 'pages/Dashboard/contexts/viewContext';
import {
  useGetUpcomingAppointments,
  usePrintUpcomingAppointmentsPdf,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  normalizePatientVisits,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  useAppointmentStatusColors,
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  formatDate,
  formatTime,
} from 'utils/date';
import {
  getErrorMessage,
} from 'utils/errors';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';

type HeaderProps = {
  onClose: () => void;
  onPrint: () => void;
  isPrinting: boolean;
  isPrintDisabled: boolean;
};

type AppointmentMetaProps = {
  label: string;
  value: ReactNode;
};

const loadingAppointmentSkeletonKeys = [
  'appointment-skeleton-1',
  'appointment-skeleton-2',
  'appointment-skeleton-3',
];

function Header({
  onClose,
  onPrint,
  isPrinting,
  isPrintDisabled,
}: HeaderProps) {
  return (
    <div className="flex justify-between items-center gap-3 !px-4 py-3 border-b border-b-gray-200">
      <div className="flex items-center gap-2">
        <ToolTip title="Hide Upcoming Appointments">
          <IconButton onClick={onClose}>
            <CollapseIcon className="!w-4 !h-4 !text-inherit" />
          </IconButton>
        </ToolTip>
        <h4 className="font-semibold text-sm whitespace-nowrap leading-none">
          Upcoming Appointments
        </h4>
      </div>
      <Button
        color="primary"
        variant="outlined"
        type="button"
        size="small"
        onClick={onPrint}
        disabled={isPrintDisabled}
        startIcon={isPrinting ? <Spinner /> : <PrinterIcon className="!w-4 !h-4 !text-inherit" />}
      >
        {isPrinting ? 'Printing...' : 'Print'}
      </Button>
    </div>
  );
}

function AppointmentMeta({
  label,
  value,
}: AppointmentMetaProps) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="shrink-0 font-semibold text-gray-500">
        {label}
      </span>
      <span className="text-right text-gray-700">
        {value}
      </span>
    </div>
  );
}

function AppointmentCardSkeleton() {
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-testid="upcoming-appointment-skeleton"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Skeleton variant="rounded" className="!h-5 !w-20 !rounded" />
          <Skeleton variant="rounded" className="mt-2 !h-4 !w-12 !rounded" />
        </div>
        <Skeleton variant="rounded" className="!h-6 !w-32 !rounded-full" />
      </div>
      <Skeleton variant="rounded" className="mt-3 !h-4 !w-28 !rounded" />
      <div className="mt-3 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Skeleton variant="rounded" className="!h-4 !w-16 !rounded" />
          <Skeleton variant="rounded" className="!h-4 !w-36 !rounded" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <Skeleton variant="rounded" className="!h-4 !w-16 !rounded" />
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" className="!h-5 !w-5 shrink-0" />
            <Skeleton variant="rounded" className="!h-4 !w-24 !rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UpcomingAppointmentsPanel({
  patient,
  className,
  variant = 'default',
  onClose,
}: ExtensionProps) {
  const patientId = patient?.patientId ?? 0;
  const isCompactVariant = variant === 'compact';
  const [includeCancelledNoShow, setIncludeCancelledNoShow] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const isInitialFilterRenderRef = useRef(true);
  const colorsHashMap = useColorsHashMap();
  const appointmentStatusColors = useAppointmentStatusColors();
  const {
    data: appointmentsData = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useGetUpcomingAppointments(patientId, { includeCancelledNoShow });
  const {
    mutateAsync: printUpcomingAppointmentsPdf,
    isPending: isPrinting,
  } = usePrintUpcomingAppointmentsPdf();

  const upcomingAppointments = useMemo(() => {
    const normalizedAppointments = normalizePatientVisits(appointmentsData, colorsHashMap);
    return normalizedAppointments;
  }, [appointmentsData, colorsHashMap]);

  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }

    if (patientId > 0) {
      refetch();
    }

  }, [includeCancelledNoShow, patientId, refetch]);

  useAppointmentsRefresh({
    enabled: patientId > 0,
    onCreate: refetch,
    onUpdate: refetch,
    onDelete: refetch,
  });

  const handlePrint = async () => {
    try {
      setPrintError(null);
      const response = await printUpcomingAppointmentsPdf({
        patientId,
        includeCancelledNoShow,
      });
      printJS({
        printable: response.contents,
        type: 'pdf',
        base64: true,
        showModal: true,
      });
    } catch (error) {
      console.error('Error printing upcoming appointments:', error);
      setPrintError('Failed to generate the upcoming appointments PDF. Please try again.');
    }
  };

  const hasAppointments = upcomingAppointments.length > 0;
  const isPrintDisabled = !hasAppointments || isLoading || isPrinting || patientId === 0;
  const showFooter = !isCompactVariant || Boolean(printError) || (!hasAppointments && !isLoading && !isError);

  return (
    <div className={clsx('flex flex-col h-full bg-gray-100', className)}>
      <Header
        onClose={onClose}
        onPrint={handlePrint}
        isPrinting={isPrinting}
        isPrintDisabled={isPrintDisabled}
      />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mb-4">
          <Checkbox
            id="toggle-include-cancelled-no-show-upcoming-appointments"
            checked={includeCancelledNoShow}
            className={includeCancelledNoShow ? '!text-blue-500' : ''}
            label="Include Cancelled & No-show"
            onChange={setIncludeCancelledNoShow}
          />
        </div>
        {isLoading && (
          <div
            className="flex flex-col gap-3"
            data-testid="upcoming-appointments-loading"
          >
            {loadingAppointmentSkeletonKeys.map((skeletonKey) => (
              <AppointmentCardSkeleton key={skeletonKey} />
            ))}
          </div>
        )}
        {isError && !isLoading && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-semibold">
              Unable to load upcoming appointments.
            </p>
            <p className="mt-1 text-xs text-red-600">
              {getErrorMessage(error) ?? 'Please try again.'}
            </p>
            <Button
              className="!mt-4"
              variant="outlined"
              onClick={() => {
                refetch();
              }}
            >
              Retry
            </Button>
          </div>
        )}
        {!isLoading && !isError && !hasAppointments && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
            No upcoming appointments.
          </div>
        )}
        {!isLoading && !isError && hasAppointments && (
          <div className="flex flex-col gap-3">
            {upcomingAppointments.map((appointment) => {
              const providerName = appointment.provider?.formattedNames?.fullNameWithCredentials ?? DEFAULT_EMPTY_VALUE;
              const locationName = appointment.serviceFacility?.nickname ?? DEFAULT_EMPTY_VALUE;
              const dateLabel = !isNil(appointment.visitDateTime)
                ? formatDate(appointment.visitDateTime)
                : DEFAULT_EMPTY_VALUE;
              const timeLabel = !isNil(appointment.visitDateTime)
                ? formatTime(appointment.visitDateTime)
                : DEFAULT_EMPTY_VALUE;
              const appointmentStatusColor = appointmentStatusColors[appointment.patientAppointmentStatus ?? ''];

              return (
                <div
                  key={`${appointment.encounterId ?? providerName}`}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">
                        {dateLabel}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {timeLabel}
                      </p>
                    </div>
                    {notEmptyString(appointment?.patientAppointmentStatus) && (
                      <AppointmentStatusSelect
                        classes={{
                          root: clsx('!w-fit shrink-0', {
                            'max-w-[11rem]': isCompactVariant,
                          }),
                          label: 'text-gray-700 text-xs',
                          select: '!py-0 rounded !font-medium',
                        }}
                        disabled
                        value={appointment.patientAppointmentStatus ?? ''}
                        encounterId={appointment.encounterId}
                        appointmentDate={appointment.visitDateTime}
                        statusColor={appointmentStatusColor}
                      />
                    )}
                  </div>
                  <div className="mt-3 flex flex-col gap-2">
                    {notEmptyString(appointment?.typeOfVisitDescription) && (
                      <AppointmentMeta
                        label="Type of Visit"
                        value={(
                          <Tag
                            className="!inline-block !max-w-full truncate !leading-[18px]"
                            tagName={appointment.typeOfVisitDescription ?? ''}
                            tooltip={appointment.typeOfVisitDescription}
                          />
                        )}
                      />
                    )}
                    <AppointmentMeta
                      label="Provider"
                      value={providerName}
                    />
                    <AppointmentMeta
                      label="Location"
                      value={isCompactVariant
                        ? locationName
                        : (
                          <span className="inline-flex items-center gap-2">
                            <Image
                              size="xs"
                              className="font-semibold"
                              color={appointment.serviceFacilityColor ?? ''}
                            >
                              <BusinessRoundedIcon className="!w-4 !h-4 !text-white" />
                            </Image>
                            <span>{locationName}</span>
                          </span>
                        )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showFooter && (
        <div className="border-t border-t-gray-200 bg-white px-4 py-4">
          {printError && (
            <p className="text-xs text-red-600">
              {printError}
            </p>
          )}
          {!hasAppointments && !isLoading && !isError && (
            <p className={clsx('text-xs text-gray-500', {
              'mt-2': printError,
            })}>
              There are no future appointments to print.
            </p>
          )}
          {!isLoading && !isError && !isPrinting && !hasAppointments && patientId === 0 && (
            <p className={clsx('text-xs text-gray-500', {
              'mt-2': !printError,
              'mt-1': printError,
            })}>
              Select a patient to print upcoming appointments.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
